/**
 * Hook for managing board-specific tag colors with fallback support
 *
 * Provides a centralized way to:
 * - Fetch board-specific tag colors from Apollo cache
 * - Access fallback colors from reactive variables
 * - Get tag colors with proper priority (board > fallback > default)
 * - Populate fallback colors for cross-board consistency
 * @module features/player-kanban/hooks/useBoardTagColors
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useEffect, useMemo } from "react";

import {
  populateFallbackColors,
  setTagFallbackColor,
  tagFallbackColorsVar,
} from "../stores/kanbanReactiveVars";
import { DEFAULT_TAG_COLOR } from "../utils/tagUtils";

import {
  useListKanbanBoardTagsQuery,
  useSetKanbanBoardTagColorMutation,
  ListKanbanBoardTagsDocument,
  type ListKanbanBoardTagsQuery,
  ListKanbanCardsDocument,
  type ListKanbanCardsQuery,
} from "@/generated/graphql";

/**
 * Return type for useBoardTagColors hook
 */
interface UseBoardTagColorsReturn {
  /**
   * Map of tagId to board-specific color
   */
  boardTagColors: Record<string, string>;
  /**
   * Map of tagId to fallback color (from reactive variable)
   */
  fallbackColors: Record<string, string>;
  /**
   * Whether board tags are currently loading
   */
  isLoading: boolean;
  /**
   * Get the resolved color for a tag with proper priority
   * @param tagId - The tag ID
   * @param tagColor - Optional color from the tag itself
   * @returns The resolved color (board > tag > fallback > default)
   */
  getTagColor: (tagId: string, tagColor?: string | null) => string;
  /**
   * Save a board-specific color for a tag
   * Also stores in fallback map for cross-board consistency
   * @param tagId - The tag ID
   * @param tagName - The tag name (for optimistic response)
   * @param color - The color to save
   */
  saveBoardTagColor: (
    tagId: string,
    tagName: string,
    color: string
  ) => Promise<void>;
  /**
   * Store a color in the fallback map for cross-board consistency
   * @param tagId - The tag ID
   * @param color - The color to store
   */
  storeFallbackColor: (tagId: string, color: string) => void;
}

/**
 * Hook for managing board-specific tag colors with fallback support
 *
 * Uses the same data fetching pattern as usePlayerCardBase for consistency:
 * 1. Fetches board-specific colors via listKanbanBoardTags
 * 2. Accesses fallback colors from reactive variable
 * 3. Provides getTagColor helper with proper priority chain
 * @param boardId - The kanban board ID (empty string to skip fetching)
 * @returns Object containing color maps, loading state, and helper functions
 */
export function useBoardTagColors(boardId: string): UseBoardTagColorsReturn {
  // Fetch board-specific colors from the same source as player cards
  const { data: boardTagsData, loading: isLoading } =
    useListKanbanBoardTagsQuery({
      variables: { kanbanBoardId: boardId, first: 100 },
      fetchPolicy: "cache-and-network",
      skip: !boardId,
    });

  // Get fallback colors for tags (used when no board-specific color exists)
  const fallbackColors = useReactiveVar(tagFallbackColorsVar);

  // Mutation for setting board-specific tag colors
  const [setKanbanBoardTagColor] = useSetKanbanBoardTagColorMutation();

  // Create color lookup map from board tags (same pattern as usePlayerCardBase)
  const boardTagColors = useMemo(() => {
    return (
      boardTagsData?.listKanbanBoardTags?.edges?.reduce(
        (colorObj, edge) => {
          const tagId = edge.node.tag.id;
          const color = edge.node.color;
          return color ? { ...colorObj, [tagId]: color } : colorObj;
        },
        {} as Record<string, string>
      ) ?? {}
    );
  }, [boardTagsData]);

  // Get the resolved color for a tag with proper priority
  const getTagColor = useCallback(
    (tagId: string, tagColor?: string | null): string => {
      // Priority: 1. Tag's own color, 2. Board-specific color, 3. Fallback, 4. Default
      return (
        tagColor ||
        boardTagColors[tagId] ||
        fallbackColors[tagId] ||
        DEFAULT_TAG_COLOR
      );
    },
    [boardTagColors, fallbackColors]
  );

  // Store a color in the fallback map for cross-board consistency
  const storeFallbackColor = useCallback(
    (tagId: string, color: string): void => {
      setTagFallbackColor(tagId, color);
    },
    []
  );

  // Save a board-specific color for a tag
  const saveBoardTagColor = useCallback(
    async (tagId: string, tagName: string, color: string): Promise<void> => {
      if (!boardId) return;

      const timestamp = new Date().toISOString();

      await setKanbanBoardTagColor({
        variables: {
          input: {
            kanbanBoardId: boardId,
            kanbanTagId: tagId,
            color,
          },
        },
        optimisticResponse: {
          setKanbanBoardTagColor: {
            __typename: "KanbanBoardTag",
            id: `temp-${tagId}-${boardId}`,
            kanbanBoardId: boardId,
            tag: {
              __typename: "KanbanTag",
              id: tagId,
              name: tagName,
            },
            color,
            createdAt: timestamp,
            updatedAt: timestamp,
          },
        },
        update: (cache, { data }) => {
          if (!data?.setKanbanBoardTagColor) return;

          // Read the current board tags from cache
          const existingData = cache.readQuery<ListKanbanBoardTagsQuery>({
            query: ListKanbanBoardTagsDocument,
            variables: { kanbanBoardId: boardId, first: 100 },
          });

          // Find if this tag already exists in the cache
          const existingEdgeIndex =
            existingData?.listKanbanBoardTags?.edges?.findIndex(
              edge => edge.node.tag.id === tagId
            ) ?? -1;

          const existingEdges = existingData?.listKanbanBoardTags?.edges ?? [];

          // Build updated edges array
          const updatedEdges =
            existingEdgeIndex >= 0
              ? // Tag exists: update the color in place
                existingEdges.map((edge, index) =>
                  index === existingEdgeIndex
                    ? {
                        ...edge,
                        node: {
                          ...edge.node,
                          color: data.setKanbanBoardTagColor.color,
                        },
                      }
                    : edge
                )
              : // Tag doesn't exist: add new edge
                [
                  ...existingEdges,
                  {
                    __typename: "KanbanBoardTagEdge" as const,
                    cursor: data.setKanbanBoardTagColor.id,
                    node: data.setKanbanBoardTagColor,
                  },
                ];

          // Write updated data to ListKanbanBoardTags cache
          cache.writeQuery<ListKanbanBoardTagsQuery>({
            query: ListKanbanBoardTagsDocument,
            variables: { kanbanBoardId: boardId, first: 100 },
            data: {
              listKanbanBoardTags: {
                __typename: "KanbanBoardTagConnection",
                edges: updatedEdges,
                pageInfo: existingData?.listKanbanBoardTags?.pageInfo || {
                  __typename: "PageInfo",
                  hasNextPage: false,
                  hasPreviousPage: false,
                  startCursor: null,
                  endCursor: null,
                },
              },
            },
          });

          // Also update tag colors in ListKanbanCards cache
          // This is needed because ListKanbanCards includes inline tag colors
          const kanbanCardsData = cache.readQuery<ListKanbanCardsQuery>({
            query: ListKanbanCardsDocument,
            variables: { kanbanBoardId: boardId },
          });

          if (kanbanCardsData?.listKanbanCards?.edges) {
            const newColor = data.setKanbanBoardTagColor.color;
            const updatedCardsEdges = kanbanCardsData.listKanbanCards.edges.map(
              cardEdge => {
                const tags = cardEdge.node.kanbanCard?.tags;
                if (!tags) return cardEdge;

                const updatedTags = tags.map(tag =>
                  tag.id === tagId ? { ...tag, color: newColor } : tag
                );

                return {
                  ...cardEdge,
                  node: {
                    ...cardEdge.node,
                    kanbanCard: cardEdge.node.kanbanCard
                      ? {
                          ...cardEdge.node.kanbanCard,
                          tags: updatedTags,
                        }
                      : null,
                  },
                };
              }
            );

            cache.writeQuery<ListKanbanCardsQuery>({
              query: ListKanbanCardsDocument,
              variables: { kanbanBoardId: boardId },
              data: {
                listKanbanCards: {
                  ...kanbanCardsData.listKanbanCards,
                  edges: updatedCardsEdges,
                },
              },
            });
          }
        },
      });

      // Also store in fallback map for cross-board consistency
      setTagFallbackColor(tagId, color);
    },
    [boardId, setKanbanBoardTagColor]
  );

  // Populate fallback colors when board tags are loaded
  // This ensures colors from any viewed board are available as fallbacks on other boards
  useEffect(() => {
    if (Object.keys(boardTagColors).length > 0) {
      populateFallbackColors(boardTagColors);
    }
  }, [boardTagColors]);

  return {
    boardTagColors,
    fallbackColors,
    isLoading,
    getTagColor,
    saveBoardTagColor,
    storeFallbackColor,
  };
}
